<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import Dinero from 'dinero.js'
import { ExclamationCircleIcon } from '@heroicons/vue/20/solid'

const props = defineProps<{
  label: string;
  description?: string;
  default?: any;
  icon?: any;
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', dinero: Dinero.Dinero | null): void
}>();

const price = ref(props.default || '0');
const currency = ref('EUR');
const hasError = ref(false);
const currencies = ['EUR', 'USD', 'GBP', 'AUD', 'CAD', 'DZD', 'JPY', 'CHF', 'CNY', 'AED'] as const;

const inputClasses = computed(() => {
  return hasError.value 
    ? "block w-full rounded-md border-0 py-1.5 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500 sm:text-sm sm:leading-6"
    : "block w-full rounded-md border-0 py-1.5 pr-10 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6";
});

const validatePrice = () => {
  hasError.value = isNaN(parseFloat(price.value));
}

const parsedPrice = computed(() => {
  if (hasError.value) return;

  try {
    return Dinero({
      amount: parseInt((parseFloat(price.value) * 100).toFixed(2)),
      currency: (currency.value as typeof currencies[number]) || "EUR",
      precision: 2,
    });
  } catch (error) {
    console.error("Price error:", error, price.value)
    return;
  }
});

const defaultPrice = Dinero({ amount: 0, currency: "EUR", precision: 2 })
watch(currency, validatePrice);
watch(parsedPrice, (newValue) => emits("update:modelValue", newValue?.getAmount() ? newValue : defaultPrice ))
</script>
<template>
  <div>
    <label for="price" class="block text-sm font-medium leading-6 text-gray-900">{{ props.label }} :</label>
    <span v-if="props.description" class="block text-xs italic text-slate-600">({{ props.description }})</span>
    <div class="relative mt-2 rounded-md shadow-sm">
      <input 
        v-model="price" 
        id="price" 
        type="number"
        step="0.01"
        min="0"
        :class="inputClasses"
        @input="validatePrice"
        :aria-invalid="hasError"
        placeholder="Entrer le montant"
        class="block w-full rounded-md border-0 py-1.5 pl-9 pr-20 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
      />
      <div class="absolute inset-y-0 right-0 flex items-center">
        <label for="currency" class="sr-only">Currency</label>
        <select v-model="currency" id="currency" name="currency" class="h-full py-0 pl-2 text-gray-500 bg-transparent border-0 rounded-md pr-7 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm">
          <option v-for="curr in currencies" :key="curr" :value="curr">{{ curr }}</option>
        </select>
      </div>
      <div v-if="hasError" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none right-14">
        <ExclamationCircleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
      </div>
      <div v-if="icon" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none left-2">
        <component :is="icon" class="w-5 h-5 text-slate-400" aria-hidden="true" />
      </div>
    </div>
    <p v-if="hasError" class="mt-2 text-sm text-red-600" id="price-error">Merci d'entrer un montant valide.</p>
    <!-- <div class="mt-2">
      Montant: {{ (parsedPrice?.getAmount() || 0) / 100 }} {{ parsedPrice?.getCurrency() }}
    </div> -->
  </div>
</template>
